import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaChevronRight,
  FaCreditCard,
  FaLock,
  FaShieldAlt,
  FaTruck,
} from "react-icons/fa";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/checkout.css";

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  country: "United States",
  address: "",
  apartment: "",
  city: "",
  region: "",
  postalCode: "",
  cardNumber: "",
  expiry: "",
  securityCode: "",
  cardName: "",
};

const money = (amount) => `$${amount.toFixed(2)}`;
const priceOf = (product) =>
  Number.parseFloat(String(product.price).replace(/,/g, "")) || 0;

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const discountRate = Number(location.state?.discountRate) || 0;
  const coupon = location.state?.coupon || "";
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((line) => line.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, { item, product }) => total + priceOf(product) * item.quantity,
    0
  );
  const discount = subtotal * discountRate;
  const shipping = subtotal >= 100 ? 0 : 9.9;
  const total = Math.max(0, subtotal - discount + shipping);

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === "cardNumber")
      nextValue = value
        .replace(/\D/g, "")
        .slice(0, 16)
        .replace(/(.{4})/g, "$1 ")
        .trim();
    if (name === "expiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      nextValue =
        digits.length > 2
          ? `${digits.slice(0, 2)} / ${digits.slice(2)}`
          : digits;
    }
    if (name === "securityCode")
      nextValue = value.replace(/\D/g, "").slice(0, 4);
    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const required = [
      "email",
      "firstName",
      "lastName",
      "address",
      "city",
      "region",
      "postalCode",
      "cardNumber",
      "expiry",
      "securityCode",
      "cardName",
    ];
    const nextErrors = {};
    required.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      nextErrors.email = "Enter a valid email address.";
    if (form.cardNumber.replace(/\s/g, "").length < 16)
      nextErrors.cardNumber = "Enter a 16-digit card number.";
    if (form.expiry.replace(/\D/g, "").length < 4)
      nextErrors.expiry = "Use MM / YY format.";
    if (form.securityCode.length < 3)
      nextErrors.securityCode = "Enter a valid security code.";
    if (!acceptedTerms)
      nextErrors.terms = "Please accept the terms to place your order.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePlaceOrder = (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const orderNumber = `DZCP-${String(Date.now()).slice(-8)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: {
          orderNumber,
          total,
          email: form.email,
          customer: `${form.firstName} ${form.lastName}`,
        },
      });
    }, 650);
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-page checkout-page--empty">
        <FaCreditCard />
        <h1>There’s nothing to checkout yet.</h1>
        <p>
          Add a product to your cart, then return here to complete your order.
        </p>
        <Link to="/shop">BROWSE PRODUCTS</Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <section className="checkout-progress">
        <div className="checkout-container">
          <div className="is-complete">
            <span>1</span>
            <strong>Shopping cart</strong>
          </div>
          <i />
          <div className="is-active">
            <span>2</span>
            <strong>Checkout</strong>
          </div>
          <i />
          <div>
            <span>3</span>
            <strong>Order complete</strong>
          </div>
        </div>
      </section>

      <div className="checkout-container checkout-heading">
        <div>
          <span>SECURE CHECKOUT</span>
          <h1>Complete your order</h1>
        </div>
        <Link to="/cartpage">
          <FaChevronLeft /> Return to cart
        </Link>
      </div>

      <div className="checkout-container checkout-layout">
        <form className="checkout-form" onSubmit={handlePlaceOrder} noValidate>
          <section className="checkout-section">
            <div className="checkout-section__title">
              <span>1</span>
              <div>
                <h2>Contact information</h2>
                <p>Your receipt and tracking details will be sent here.</p>
              </div>
            </div>
            <Field
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              error={errors.email}
              onChange={updateField}
            />
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title">
              <span>2</span>
              <div>
                <h2>Delivery address</h2>
                <p>
                  Enter the address where you would like to receive your order.
                </p>
              </div>
            </div>
            <div className="checkout-form__grid">
              <Field
                label="First name"
                name="firstName"
                autoComplete="given-name"
                value={form.firstName}
                error={errors.firstName}
                onChange={updateField}
              />
              <Field
                label="Last name"
                name="lastName"
                autoComplete="family-name"
                value={form.lastName}
                error={errors.lastName}
                onChange={updateField}
              />
            </div>
            <Field
              label="Company (optional)"
              name="company"
              autoComplete="organization"
              value={form.company}
              onChange={updateField}
            />
            <label className="checkout-field">
              <span>Country / region</span>
              <select
                name="country"
                value={form.country}
                onChange={updateField}
                autoComplete="country-name"
              >
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Australia</option>
                <option>Germany</option>
                <option>France</option>
                <option>Singapore</option>
              </select>
            </label>
            <Field
              label="Street address"
              name="address"
              autoComplete="street-address"
              value={form.address}
              error={errors.address}
              onChange={updateField}
            />
            <Field
              label="Apartment, suite, unit (optional)"
              name="apartment"
              value={form.apartment}
              onChange={updateField}
            />
            <div className="checkout-form__grid checkout-form__grid--three">
              <Field
                label="Town / city"
                name="city"
                autoComplete="address-level2"
                value={form.city}
                error={errors.city}
                onChange={updateField}
              />
              <Field
                label="State / province"
                name="region"
                autoComplete="address-level1"
                value={form.region}
                error={errors.region}
                onChange={updateField}
              />
              <Field
                label="ZIP / postal code"
                name="postalCode"
                autoComplete="postal-code"
                value={form.postalCode}
                error={errors.postalCode}
                onChange={updateField}
              />
            </div>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title">
              <span>3</span>
              <div>
                <h2>Shipping method</h2>
                <p>All parcels include online tracking.</p>
              </div>
            </div>
            <label className="checkout-shipping-option">
              <input type="radio" name="shipping" defaultChecked />
              <FaTruck />
              <span>
                <strong>Tracked standard delivery</strong>
                <small>Estimated delivery in 4–8 business days</small>
              </span>
              <b>{shipping === 0 ? "FREE" : money(shipping)}</b>
            </label>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title">
              <span>4</span>
              <div>
                <h2>Payment</h2>
                <p>All transactions are secure and encrypted.</p>
              </div>
            </div>
            <div className="checkout-payment">
              <div className="checkout-payment__head">
                <span>
                  <FaCreditCard /> Credit or debit card
                </span>
                <div>
                  <i>VISA</i>
                  <i>MC</i>
                  <i>AMEX</i>
                </div>
              </div>
              <div className="checkout-payment__body">
                <Field
                  label="Card number"
                  name="cardNumber"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={form.cardNumber}
                  error={errors.cardNumber}
                  onChange={updateField}
                />
                <div className="checkout-form__grid">
                  <Field
                    label="Expiration date (MM / YY)"
                    name="expiry"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    value={form.expiry}
                    error={errors.expiry}
                    onChange={updateField}
                  />
                  <Field
                    label="Security code"
                    name="securityCode"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    value={form.securityCode}
                    error={errors.securityCode}
                    onChange={updateField}
                  />
                </div>
                <Field
                  label="Name on card"
                  name="cardName"
                  autoComplete="cc-name"
                  value={form.cardName}
                  error={errors.cardName}
                  onChange={updateField}
                />
              </div>
            </div>
          </section>

          <label className="checkout-terms">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => setAcceptedTerms(event.target.checked)}
            />
            <span>
              I have read and agree to the website{" "}
              <Link to="/terms">terms and conditions</Link> and{" "}
              <Link to="/privacy">privacy policy</Link>.
            </span>
          </label>
          {errors.terms && (
            <p className="checkout-terms-error">{errors.terms}</p>
          )}
          <button
            type="submit"
            className="checkout-submit"
            disabled={submitting}
          >
            <FaLock />
            {submitting ? "PROCESSING ORDER…" : `PLACE ORDER — ${money(total)}`}
            <FaChevronRight />
          </button>
          <p className="checkout-security">
            <FaShieldAlt /> Your payment information is encrypted and never
            stored in this demo storefront.
          </p>
        </form>

        <aside className="checkout-summary">
          <h2>
            Your order{" "}
            <span>
              {lines.reduce((count, line) => count + line.item.quantity, 0)}{" "}
              items
            </span>
          </h2>
          <div className="checkout-summary__products">
            {lines.map(({ item, product }) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div>
                  <img src={product.image} alt={product.name} />
                  <span>{item.quantity}</span>
                </div>
                <section>
                  <Link to={`/product/${product.id}`}>{product.name}</Link>
                  <small>
                    {item.color || product.colors?.[0]}
                    {item.size && item.size !== "One size"
                      ? ` / ${item.size}`
                      : ""}
                  </small>
                </section>
                <strong>{money(priceOf(product) * item.quantity)}</strong>
              </article>
            ))}
          </div>
          <div className="checkout-summary__totals">
            <p>
              <span>Subtotal</span>
              <strong>{money(subtotal)}</strong>
            </p>
            {discount > 0 && (
              <p className="is-discount">
                <span>Discount ({coupon})</span>
                <strong>−{money(discount)}</strong>
              </p>
            )}
            <p>
              <span>Shipping</span>
              <strong>{shipping === 0 ? "Free" : money(shipping)}</strong>
            </p>
            <div>
              <span>
                Total <small>USD</small>
              </span>
              <strong>{money(total)}</strong>
            </div>
          </div>
          <div className="checkout-summary__benefits">
            <p>
              <FaTruck />
              <span>
                <strong>Tracked shipping</strong>Updates sent to your email
              </span>
            </p>
            <p>
              <FaShieldAlt />
              <span>
                <strong>Buyer protection</strong>30-day return window
              </span>
            </p>
          </div>
        </aside>
      </div>
    </main>
  );
};

const Field = ({ label, error, ...inputProps }) => (
  <label className={`checkout-field ${error ? "has-error" : ""}`}>
    <span>{label}</span>
    <input {...inputProps} />
    {error && <small>{error}</small>}
  </label>
);

export default CheckOut;
